import { all, put, select, takeLatest } from 'redux-saga/effects';
import { AuthActionTypes } from '@store/auth/auth-actions';
import { AlertActionType } from '@store/alert/alert-action';
import { authStore } from '@store/auth/auth-store';
import { SendVerificationCodeResponse, VerifyCodeResponse } from '@store/auth/auth';
import checkVerifyCode from '@api/clients/verify-code';
import getInfo from '@api/clients/get-info';
import getOrdersCount from '@api/financials/get-orders-count';
import getProvincesList from '@api/locations/get-provinces-list';
import getWalletCredit from '@api/financials/get-wallet-credit';
import sendVerificationCode from '@api/clients/send-verification-code';
import ReactGA from 'react-ga4';
import saveToCookie from '@utils/cookies/save-to-cookie';
import removeFromCookie from '@utils/cookies/remove-from-cookie';
import { BasketActionTypes } from '@store/basket/basket-actions';
import { homeStore } from '@store/home-screen/home-store';
import { PharmaciesActionTypes } from '@store/pharmacies/pharmacies-actions';
import localforage from 'localforage';
import editClientProfile from '@api/clients/edit-profile';
import removeFromLocal from '@utils/storage/remove-from-local';

function* getClientInfoWatcher() {
  const { navigation } = yield select(homeStore);
  try {
    const { redirectRoute } = yield select(authStore);
    const response: { data: { first_name: string, last_name: string, mobile: string, national_id: string, routeKey: string, sex: string, image: { key_0: { url: string } } } } = yield getInfo({ cache: 'no-cache' });
    if (response?.data) {
      yield put({
        type: AuthActionTypes.SET_CLIENT_INFO,
        data: {
          isCompleted: !!(response?.data?.first_name && response?.data?.last_name && response?.data?.national_id && response?.data?.sex),
          clientInfo: response?.data,
        },
      });
      if (redirectRoute) {
        yield put({
          type: AuthActionTypes.SET_REDIRECT_ROUTE,
          data: {
            redirectRoute: undefined,
          },
        });
        yield navigation.replace(redirectRoute);
      }
    }
  } catch (error: any) {
    yield put({
      type: AlertActionType.SHOW_ALERT,
      text: error?.message || 'در گرفتن اطلاعات شخصی شما مشکلی پیش آمده است، لطفا مجددا وارد سایت شوید.',
      severity: 'danger',
    });
    if (error?.code === 403) {
      removeFromCookie('token');
      removeFromLocal('otc_list');
      yield localforage.removeItem('fcm_token');
      yield put({ type: AuthActionTypes.LOGOUT });
      navigation.refresh();
    }
  }
}

function* getProfileClientInfoWatcher() {
  try {
    const orderTotalCountResponse: { data: object } = yield getOrdersCount({ cache: 'no-cache' });
    const walletInfoResponse: { data: { total: string, cashback: string, refundable: string } } = yield getWalletCredit({ cache: 'no-cache' });
    yield put({
      type: AuthActionTypes.SET_PROFILE_CLIENT_INFO,
      data: {
        walletCredit: walletInfoResponse?.data,
        orderTotalCount: orderTotalCountResponse?.data,
      },
    });
  } catch (error: any) {
    yield put({
      type: AlertActionType.SHOW_ALERT,
      text: error?.message || 'در گرفتن اطلاعات پروفایل شما وقفه ای پیش آمده است، لطفا مجدداصفحه را بارگزاری نمایید.',
      severity: 'danger',
    });
  }
}

function* sendVerificationCodeWatcher() {
  try {
    const { phoneNumber, codeRequestedType, captchaToken } = yield select(authStore);

    const response: SendVerificationCodeResponse = yield sendVerificationCode({
      mobile: phoneNumber,
      type: codeRequestedType,
      token: captchaToken,
      cache: 'no-cache',
    });

    if (response?.data?.validation_code_lifetime) {
      yield put({
        type: AuthActionTypes.SET_VALIDATION_CODE_LIFETIME,
        data: {
          validationCodeLifetime: response?.data?.validation_code_lifetime,
        },
      });
      yield put({
        type: AuthActionTypes.SET_VALIDATION_CODE_LOADING,
        data: {
          sendVerificationLoading: false,
        },
      });
      yield put({
        type: AlertActionType.SHOW_ALERT,
        text: codeRequestedType === 'call' ? 'لطفا منتظر تماس صوتی جهت اعلام کد اعتبار سنجی باشید.' : 'کد اعتبار سنجی برای شما ارسال شد',
        severity: 'success',
      });
    }
  } catch (error: any) {
    if (error?.code === 400) {
      yield put({
        type: AlertActionType.SHOW_ALERT,
        text: error?.message || 'در انجام عملیات مشکلی پیش آمده است. لطفا با پشتیبانی تماس بگیرید',
        severity: 'warning',
      });
    }

    yield put({
      type: AuthActionTypes.SET_VALIDATION_CODE_LOADING,
      data: {
        sendVerificationLoading: false,
      },
    });
    yield put({
      type: AlertActionType.SHOW_ALERT,
      text: error?.message || 'در گرفتن اطلاعات شما مشکلی پیش آمده است، لطفا بعدا مراجعه نمایید',
      severity: 'danger',
    });
  }
}

export function* checkVerifyCodeWatcher() {
  try {
    const { phoneNumber, verifyCode, callBackTypeId } = yield select(authStore);
    const response: VerifyCodeResponse = yield checkVerifyCode({
      mobile: phoneNumber,
      validationCode: verifyCode,
      cache: 'no-cache',
    });
    if (response?.data?.access_token) {
      saveToCookie('token', response?.data?.access_token);
      // @ts-ignore
      if (window && window.ReactNativeWebView) {
        // @ts-ignore
        window.ReactNativeWebView.postMessage('token');
      }
      yield put({
        type: AuthActionTypes.SAVE_TOKEN,
        data: {
          accessToken: response?.data?.access_token,
          tokenType: response?.data?.token_type,
        },
      });

      yield getClientInfoWatcher();
      yield put({ type: BasketActionTypes.GET_BASKET_INFO });
      yield put({
        type: AlertActionType.SHOW_ALERT,
        text: 'ورود شما با موفقیت انجام شد',
        severity: 'success',
      });
      yield put({
        type: AuthActionTypes.SET_LOGIN_MODAL,
        data: {
          loginModal: false,
          callBackTypeId: undefined,
          redirectRoute: undefined,
          hasCallbackFunction: false,
        },
      });
      ReactGA.ga('send', 'event', 'authentisch', 'login', 'login');
      if (callBackTypeId === 'pharmacy') {
        yield put({
          type: PharmaciesActionTypes.PHARMACY_DATA_VALIDATION,
        });
      }
      if (callBackTypeId === 'checkLockCard') {
        yield put({
          type: PharmaciesActionTypes.HANDLE_CARD_VALIDATION,
        });
      }
      if (callBackTypeId === 'addNewCard') {
        yield put({
          type: PharmaciesActionTypes.SET_NEW_DRUG_VALIDATION,
        });
      }
    }
  } catch (error: any) {
    yield put({
      type: AuthActionTypes.SET_CONFIRM_LOADING,
      data: {
        confirmLoading: false,
      },
    });
    yield put({
      type: AlertActionType.SHOW_ALERT,
      text: error?.message || 'کد اعتبار سنجی صحیح نمی باشد، لطفا کد را با دقت بیشتری وارد نمایید.',
      severity: 'danger',
    });
  }
}

export function* editClientInfoWatcher() {
  const { firstName, lastName, sex, nationalId, passportNumber, dateOfBirth, email, shebaNumber } = yield select(authStore);

  try {
    yield editClientProfile({
      dateOfBirth: dateOfBirth,
      passportNumber: passportNumber,
      lastName: lastName,
      sex: sex,
      nationalId: nationalId,
      firstName: firstName,
      email: email,
      shebaNumber: shebaNumber ? `IR${shebaNumber}` : shebaNumber,
      cache: 'no-cache',
    });

    yield put({ type: AuthActionTypes.GET_CLIENT_INFO });

    yield put({ type: AuthActionTypes.SET_SUBMIT_LOADING });

    yield put({
      type: AlertActionType.SHOW_ALERT,
      text: 'اطلاعات شما با موفقیت ویرایش شد',
      severity: 'success',
    });
  } catch (error: any) {
    yield put({
      type: AlertActionType.SHOW_ALERT,
      text: error?.message || 'در گرفتن اطلاعات شما مشکلی پیش آمده است، لطفا بعدا مراجعه نمایید',
      severity: 'danger',
    });
    yield put({ type: AuthActionTypes.SET_SUBMIT_LOADING });
  }
}

function* getProvincesListWatcher() {
  try {
    const response: { data: object } = yield getProvincesList({ cache: 'no-cache' });

    yield put({
      type: AuthActionTypes.SET_PROVINCES_LIST,
      data: response?.data,
    });
  } catch (error: any) {
    yield put({
      type: AlertActionType.SHOW_ALERT,
      text: error?.message || 'در گرفتن اطلاعات استان ها مشکلی پیش آمده است',
      severity: 'danger',
    });
    yield put({ type: AuthActionTypes.SET_SUBMIT_LOADING });
  }
}

function* setValidationCodeWatcher() {
  const { verifyCode, hasCostumeVerify } = yield select(authStore);

  if (verifyCode && verifyCode.length === 4) {
    if (hasCostumeVerify) {
      yield put({ type: AuthActionTypes.SET_HAS_CALLBACK_FUNCTION });
    } else {
      yield put({ type: AuthActionTypes.CHECK_VERIFY_CODE });
    }
  }
}

function* completeClientProfileWatcher() {
  const { firstNameError, lastNameError, nationalIdError, sexError, firstNameErrorMessage, lastNameErrorMessage, nationalIdErrorMessage, sexErrorMessage } = yield select(authStore);

  if (!firstNameError && !lastNameError && !nationalIdError && !sexError) {
    yield editClientInfoWatcher();
  } else {
    yield put({
      type: AlertActionType.SHOW_ALERT,
      text: firstNameErrorMessage + '\n' +
        lastNameErrorMessage + '\n' +
        nationalIdErrorMessage + '\n' +
        sexErrorMessage,
      severity: 'danger',
    });
    yield put({ type: AuthActionTypes.SET_SUBMIT_LOADING });
  }
}

function* authMiddleware() {
  yield all([
    takeLatest(AuthActionTypes.GET_CLIENT_INFO, getClientInfoWatcher),
    takeLatest(AuthActionTypes.GET_PROFILE_CLIENT_INFO, getProfileClientInfoWatcher),
    takeLatest(AuthActionTypes.SEND_VERIFICATION_CODE, sendVerificationCodeWatcher),
    takeLatest(AuthActionTypes.CHECK_VERIFY_CODE, checkVerifyCodeWatcher),
    takeLatest(AuthActionTypes.CHECK_SMS_CODE_AND_LOGIN, checkVerifyCodeWatcher),
    takeLatest(AuthActionTypes.EDIT_CLIENT_INFO, editClientInfoWatcher),
    takeLatest(AuthActionTypes.GET_PROVINCES_LIST, getProvincesListWatcher),
    takeLatest(AuthActionTypes.SET_VALIDATION_CODE, setValidationCodeWatcher),
    takeLatest(AuthActionTypes.COMPLETE_CLIENT_PROFILE, completeClientProfileWatcher),
  ]);
}

export default authMiddleware;
